import { useState } from 'react';
import { Plus, Search, Filter, Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { AccountGroupsList } from '../components/AccountGroupsList';
import { AccountGroupFormModal } from '../components/AccountGroupFormModal';
import { AccountGroup } from '../hooks/useAccountGroupsApi';

const ACCOUNT_TYPES = ['All', 'Asset', 'Liability', 'Equity', 'Income', 'COGS', 'Expense'];

export function AccountGroupsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AccountGroup | null>(null);

  const handleEdit = (group: AccountGroup) => {
    setEditingGroup(group);
    setIsModalOpen(true);
  };

  const handleCreateNew = () => {
    setEditingGroup(null);
    setIsModalOpen(true);
  };

  return (
    <div className="max-w-[1580px] mx-auto space-y-8 pb-16">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            Account Groups 
            <span className="inline-block animate-bounce origin-bottom">✨</span>
          </h1>
          <p className="text-gray-500 mt-1 text-sm md:text-base">
            Manage your financial categorization and account groupings.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="text-gray-600">
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </Button>
          <Button onClick={handleCreateNew} className="bg-pink-600 hover:bg-pink-700 text-white shadow-sm shadow-pink-500/20">
            <Plus className="w-4 h-4 mr-2" />
            Create New Group
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            placeholder="Search account groups..." 
            className="pl-9 bg-gray-50/50 border-gray-200"
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <Filter className="w-4 h-4 text-gray-400 hidden md:block" />
          <div className="flex flex-wrap gap-2">
            {ACCOUNT_TYPES.map(type => (
              <button
                key={type}
                onClick={() => { setTypeFilter(type); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  typeFilter === type 
                    ? 'bg-gray-900 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List Section */}
      <AccountGroupsList 
        searchQuery={searchQuery}
        typeFilter={typeFilter}
        page={page}
        onPageChange={setPage}
        onEdit={handleEdit}
      />

      {/* Form Modal */}
      <AccountGroupFormModal 
        open={isModalOpen} 
        onOpenChange={setIsModalOpen}
        editGroup={editingGroup}
      />
    </div>
  );
}
